<script lang="ts">
	import { createEventDispatcher } from 'svelte';
	import { format } from 'svelte-i18n';
	import { ButtonV2, Container, Modal, Text } from '@Shelly/shelly-design-system';
	import { capFirstChar, openExternalLink } from 'src/helpers/utilities';
	import { NEW_APP_LAYOUT_DISCORD_LINK } from 'src/constants';
	import { SystemScreenBreakpoints } from '@Shelly/shelly-design-system/helpers/enums';

	export let showModal = false;
	export let isEnabling = true;

	const dispatch = createEventDispatcher();
	let currentStep = 0;
	let touchStartX = 0;
	let windowWidth = 0;

	$: isMobile = windowWidth <= SystemScreenBreakpoints.MOBILE_MAX;
	const SWIPE_THRESHOLD = 50;

	const handleTouchStart = (e: TouchEvent) => {
		touchStartX = e.changedTouches[0].screenX;
	};

	const handleTouchEnd = (e: TouchEvent) => {
		if (window.innerWidth > SystemScreenBreakpoints.MOBILE_MAX) return;

		const diff = touchStartX - e.changedTouches[0].screenX;
		if (Math.abs(diff) < SWIPE_THRESHOLD) return;

		if (diff > 0 && !isLastStep) {
			currentStep += 1;
		} else if (diff < 0 && currentStep > 0) {
			currentStep -= 1;
		}
	};

	const swipeNav = (node: HTMLElement) => {
		node.addEventListener('touchstart', handleTouchStart, { passive: true });
		node.addEventListener('touchend', handleTouchEnd);
		return {
			destroy() {
				node.removeEventListener('touchstart', handleTouchStart);
				node.removeEventListener('touchend', handleTouchEnd);
			},
		};
	};

	const ONBOARDING_IMG_PATH = './images/onboarding/new_layout';

	const enableSteps = [
		{
			heading: $format('onboarding_new_layout_title', {
				default: 'Welcome to the redesigned Shelly UI',
			}),
			description: $format('onboarding_new_layout_step1_description', {
				default:
					"We've redesigned Shelly to feel cleaner, faster, and more focused. The new panel-based layout helps you spend less time navigating and more time controlling your devices.",
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_new_step_1.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_new_step_1.png`,
		},
		{
			heading: $format('onboarding_new_layout_step2_heading', {
				default: 'Centralized settings',
			}),
			description: $format('onboarding_new_layout_step2_description', {
				default:
					'Device and app settings are now grouped in the top right. Everything related to configuration is easy to find, without cluttering your everyday workflow.',
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_new_step_2.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_new_step_2.png`,
		},
		{
			heading: $format('onboarding_new_layout_step3_heading', {
				default: 'Grouped dashboard view',
			}),
			description: $format('onboarding_new_layout_step3_description', {
				default:
					'In the new layout, dashboards are organized in a grouped grid view instead of a horizontal sliding bar at the top. This structure allows users to see all available dashboards at once, making navigation clearer and reducing clutter as more dashboards are added.',
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_new_step_3.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_new_step_3.png`,
		},
		{
			heading: $format('onboarding_new_layout_step4_heading', {
				default: 'Split dashboard view',
			}),
			description: $format('onboarding_new_layout_step4_description', {
				default:
					'When you click on a dashboard, it opens a view divided into two sections, one on the left and one on the right, making it easy to browse and manage your devices at a glance.',
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_new_step_4.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_new_step_4.png`,
		},
		{
			heading: $format('onboarding_new_layout_step5_heading', {
				default: 'Device detail overlay',
			}),
			description: $format('onboarding_new_layout_step5_description', {
				default:
					'When a device is selected, its detail panel slides in as an overlay on top of the dashboard, without pushing or hiding the content behind it, making it easy to stay oriented while reviewing device details.',
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_new_step_5.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_new_step_5.png`,
		},
	];

	const disableSteps = [
		{
			heading: $format('onboarding_classic_layout_title', {
				default: 'Switching back to the classic Shelly UI',
			}),
			description: $format('onboarding_classic_layout_step1_description', {
				default:
					'The interface displays all panels by default, including the right-side information panel, regardless of context. Device details, weather, and notifications are always visible on screen.',
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_old_step_1.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_old_step_1.png`,
		},
		{
			heading: $format('onboarding_classic_layout_step2_heading', {
				default: 'Sidebar navigation',
			}),
			description: $format('onboarding_classic_layout_step2_description', {
				default:
					'Settings and configuration options are accessible through the sidebar menu, integrated alongside your main navigation. Everything is reachable from the same right-hand panel.',
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_old_step_2.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_old_step_2.png`,
		},
		{
			heading: $format('onboarding_classic_layout_step3_heading', {
				default: 'Horizontal dashboard tabs',
			}),
			description: $format('onboarding_classic_layout_step3_description', {
				default:
					'Dashboards are organized in a horizontal sliding bar at the top of the screen. Swiping or clicking through tabs lets you quickly switch between different device groups.',
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_old_step_3.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_old_step_3.png`,
		},
		{
			heading: $format('onboarding_classic_layout_step4_heading', {
				default: 'Full-width device view',
			}),
			description: $format('onboarding_classic_layout_step4_description', {
				default:
					'Selecting a device opens its details within the main content area. The full-width layout provides a detailed look at each device settings and controls.',
			}),
			image: `${ONBOARDING_IMG_PATH}/onboarding_old_step_4.png`,
			mobileImage: `${ONBOARDING_IMG_PATH}/onboarding_mobile_old_step_4.png`,
		},
	];

	$: steps = isEnabling ? enableSteps : disableSteps;
	$: isLastStep = currentStep === steps.length - 1;

	const handleNext = () => {
		if (isLastStep) {
			handleClose();
		} else {
			currentStep += 1;
		}
	};

	const handleClose = () => {
		showModal = false;
		currentStep = 0;
		dispatch('close');
	};
</script>

<svelte:window bind:innerWidth={windowWidth} />

<Modal hideTitle {showModal} on:close={handleClose}>
	<Container slot="content" padding="0">
		<div
			class="onboarding-slide d-flex flex-direction-column align-items-center text-center"
			use:swipeNav
		>
			<Text styleType="h1" class="mb-m onboarding-title">
				{steps[currentStep].heading}
			</Text>

			{#if !isEnabling && currentStep === 0}
				<Text class="mb-s text-300 onboarding-step-description">
					{@html $format('onboarding_classic_layout_feedback', {
						default: "Tell us what didn't work for you in the new layout",
					})}
					<span
						role="presentation"
						class="link-styles text-600"
						on:click={() => {
							handleClose();
							openExternalLink(NEW_APP_LAYOUT_DISCORD_LINK);
						}}
					>
						{$format('here', { default: 'here' })}
					</span>
				</Text>
			{/if}

			<Text class="mb-m text-300 onboarding-step-description">
				{steps[currentStep].description}
			</Text>

			<div class="illustration-container mb-m">
				<img
					class="illustration-img"
					src={isMobile ? steps[currentStep].mobileImage : steps[currentStep].image}
					alt={steps[currentStep].heading}
				/>
			</div>

			<!-- Step indicators -->
			<div class="step-indicators d-flex align-items-center justify-content-center mb-m">
				{#each steps as _, i}
					<button
						class="step-dot"
						class:active={i === currentStep}
						on:click={() => (currentStep = i)}
						aria-label={$format('onboarding_step_indicator', {
							default: 'Step {step} of {total}',
							values: { step: i + 1, total: steps.length },
						})}
					></button>
				{/each}
			</div>

			<div
				class="d-flex flex-direction-column align-items-center justify-content-center width-full"
			>
				<ButtonV2 class="onboarding-btn" variant="primary" on:click={handleNext}>
					{isLastStep
						? $format('button_close', { default: 'Close' })
						: $format('button_next', { default: 'Next' })}
				</ButtonV2>
				{#if !isLastStep}
					<p class="skip mt-m mb-0 cursor-pointer" role="presentation" on:click={handleClose}>
						{capFirstChar($format('ai_chat_popover_skip', { default: 'Skip' }))}
					</p>
				{/if}
			</div>
		</div>
	</Container>
</Modal>

<style>
	.onboarding-slide {
		background-color: #151617;
		border-radius: 8px;
	}

	:global(.onboarding-title) {
		color: white !important;
	}

	:global(.onboarding-description) {
		color: white !important;
		font-size: 20px;
		font-weight: 500;
	}

	:global(.onboarding-step-description) {
		color: rgba(255, 255, 255, 0.85) !important;
		max-width: 560px;
	}

	.illustration-container {
		width: 100%;
		max-width: 460px;
	}

	.illustration-img {
		width: 100%;
		height: auto;
		border-radius: 8px;
		object-fit: contain;
	}

	@media (max-width: 639.98px) {
		.illustration-img {
			max-height: 300px;
		}
	}

	.step-indicators {
		gap: 8px;
	}

	.step-dot {
		width: 8px;
		height: 8px;
		border-radius: 50%;
		border: none;
		background-color: rgba(255, 255, 255, 0.3);
		padding: 0;
		cursor: pointer;
		transition: background-color 0.2s ease;
	}

	.step-dot.active {
		background-color: white;
	}

	.step-dot:hover {
		background-color: rgba(255, 255, 255, 0.6);
	}

	:global(.onboarding-btn) {
		min-width: 150px;
		border-radius: 60px !important;
	}

	.skip {
		color: #a1a2a2;
	}

	.skip:hover {
		color: #868686;
	}
</style>
